import { useCallback, useEffect, useRef, useState } from "react";

const AUTO_SCROLL_THRESHOLD_PX = 48;

type UseNavLogAutoScrollOptions = {
    /** After scrolling away from the bottom, resume auto-scroll after this delay (ms). */
    resumeDelayMs?: number;
};

export function useNavLogAutoScroll(
    lines: string[],
    options: UseNavLogAutoScrollOptions = {},
) {
    const { resumeDelayMs } = options;
    const scrollRef = useRef<HTMLDivElement | null>(null);
    const stickToBottomRef = useRef(true);
    const resumeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [scrollTick, setScrollTick] = useState(0);

    const clearResumeTimer = useCallback(() => {
        if (resumeTimerRef.current != null) {
            clearTimeout(resumeTimerRef.current);
            resumeTimerRef.current = null;
        }
    }, []);

    const resetAutoScroll = useCallback(() => {
        stickToBottomRef.current = true;
        clearResumeTimer();
        setScrollTick((tick) => tick + 1);
    }, [clearResumeTimer]);

    const handleScroll = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;

        const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
        const atBottom = distanceFromBottom <= AUTO_SCROLL_THRESHOLD_PX;

        if (atBottom) {
            stickToBottomRef.current = true;
            clearResumeTimer();
            return;
        }

        stickToBottomRef.current = false;
        if (resumeDelayMs && resumeDelayMs > 0) {
            clearResumeTimer();
            resumeTimerRef.current = setTimeout(() => {
                resumeTimerRef.current = null;
                stickToBottomRef.current = true;
                setScrollTick((tick) => tick + 1);
            }, resumeDelayMs);
        }
    }, [clearResumeTimer, resumeDelayMs]);

    useEffect(() => {
        if (!stickToBottomRef.current) return;
        const el = scrollRef.current;
        if (!el) return;
        el.scrollTop = el.scrollHeight;
    }, [lines, scrollTick]);

    useEffect(() => () => clearResumeTimer(), [clearResumeTimer]);

    return {
        scrollRef,
        onScroll: handleScroll,
        resetAutoScroll,
    };
}
